import React, { useState, useMemo, useCallback, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useChangeTrackingLens, get } from '@totalsoft/change-tracking-react';
import { isDirty } from '@totalsoft/change-tracking';
import { useMutation, useConditionalSubscription } from 'features/common/security/hooks';
import { useQueryWithSimpleErrorHandling, useSimpleError, useToast, useAuthorization } from 'hooks';
import { ASSET_QUERY, ASSET_CATEGORY_QUERY, ASSET_SUBCATEGORY_QUERY } from 'features/insuranceDocument/edit/general/asset/queries';
import { DOCUMENT_HEADER_QUERY } from 'features/insuranceDocument/queries/insuranceDocumentQueries';
import { PARTNER_QUERY } from 'features/insuranceDocument/edit/general/partner/queries';
import { SAVE_OFFER_DATA } from '../policyData/mutations';
import { OFFER_DATA_SAVED } from '../policyData/subscriptions';
import OfferDataCard from './OfferDataCard';
import { dateIsGreaterThanOrEqualToCurrentDate, hasCapitalLettersAndNumbers, isCIVSeries } from 'utils/simple-validations/validations';
import { shape, required, stopOnFirstFailure, when, maxLength } from '@totalsoft/pure-validations';
import { useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import InsuranceDataContext from 'features/insuranceDocument/stepper/insuranceDataContext';
import AuthorizationCodes from 'features/common/constants/authorizationCodes';

const getValidator = (insuranceTypeCode, rcaInsuranceTypeCode, insuredIsClient) => {
  return shape({
    startDate: [required, dateIsGreaterThanOrEqualToCurrentDate] |> stopOnFirstFailure,
    insured: shape({
      street: [required |> when(insuranceTypeCode === rcaInsuranceTypeCode), maxLength(255)] |> stopOnFirstFailure,
      streetNumber: [required |> when(insuranceTypeCode === rcaInsuranceTypeCode), maxLength(50)] |> stopOnFirstFailure,
      buildingNumber: maxLength(25),
      stairNumber: maxLength(50),
      floorNumber: maxLength(50),
      flatNumber: maxLength(25),
      zipCode: maxLength(20)
    }),
    client: shape({
      street: [required |> when(insuranceTypeCode === rcaInsuranceTypeCode && !insuredIsClient), maxLength(255)] |> stopOnFirstFailure,
      streetNumber: [required |> when(insuranceTypeCode === rcaInsuranceTypeCode && !insuredIsClient), maxLength(50)] |> stopOnFirstFailure,
      buildingNumber: maxLength(25),
      stairNumber: maxLength(50),
      floorNumber: maxLength(50),
      flatNumber: maxLength(25),
      zipCode: maxLength(20)
    }),
    asset: shape({
      identityCardSeries:
        [required, hasCapitalLettersAndNumbers, isCIVSeries, maxLength(20)]
        |> stopOnFirstFailure
        |> when(insuranceTypeCode === rcaInsuranceTypeCode)
    })
  });
};

const OfferDataContainer = ({
  documentId,
  insuredId,
  clientId,
  negotiatedQuotation,
  insuranceTypeCode,
  rcaInsuranceTypeCode,
  cascoInsuranceTypeCode,
  insuranceData
}) => {
  const [insuredLens, insuredDirtyInfo, resetInsuredLens] = useChangeTrackingLens({});
  const [clientLens, clientDirtyInfo, resetClientLens] = useChangeTrackingLens({});
  const [assetLens, assetDirtyInfo, resetAssetLens] = useChangeTrackingLens({});
  const [insuranceDataLens, insuranceDataDirtyInfo, resetInsuranceDataLens] = useChangeTrackingLens({});
  const [category, setCategory] = useState(null);
  const [subcategory, setSubcategory] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveDisabled, setSaveDisabled] = useState(true);
  const [showEditableFields, setShowEditableFields] = useState(false);
  const [warningOnEditButton, setWarningOnEditButton] = useState(false);
  const validator = useMemo(
    () => getValidator(insuranceTypeCode, rcaInsuranceTypeCode, Boolean(insuredId === clientId)),
    [clientId, insuranceTypeCode, insuredId, rcaInsuranceTypeCode]
  );
  const [validation, validate] = useDirtyFieldValidation(validator);

  const { documentTypeId, documentStateId } = useContext(InsuranceDataContext);
  const hasRightToViewOfferData = useAuthorization(AuthorizationCodes.hasRightToViewOfferData, documentTypeId, documentStateId);
  const showError = useSimpleError();
  const addToast = useToast();
  const { t } = useTranslation();

  const { data: documentDataHeader } = useQueryWithSimpleErrorHandling(DOCUMENT_HEADER_QUERY, {
    variables: { documentId }
  });

  const { loading: insuredLoading } = useQueryWithSimpleErrorHandling(PARTNER_QUERY, {
    variables: { partnerId: insuredId },
    skip: !insuredId,
    onCompleted: data => {
      resetInsuredLens({ ...data?.partner });
    },
    notifyOnNetworkStatusChange: true
  });

  const { loading: clientLoading } = useQueryWithSimpleErrorHandling(PARTNER_QUERY, {
    variables: { partnerId: clientId },
    skip: !clientId || clientId === insuredId,
    onCompleted: data => {
      resetClientLens({ ...data?.partner });
    },
    notifyOnNetworkStatusChange: true
  });

  const { loading: assetLoading } = useQueryWithSimpleErrorHandling(ASSET_QUERY, {
    variables: { assetId: insuranceData.assetId, searchInactive: true },
    skip: !insuranceData?.assetId,
    onCompleted: data => {
      resetAssetLens({ ...data?.asset });
    },
    notifyOnNetworkStatusChange: true
  });

  const assetCategoryId = useMemo(() => {
    return insuranceTypeCode === rcaInsuranceTypeCode ? assetLens.rcaCategoryId |> get : assetLens.cascoCategoryId |> get;
  }, [assetLens.cascoCategoryId, assetLens.rcaCategoryId, insuranceTypeCode, rcaInsuranceTypeCode]);

  useQueryWithSimpleErrorHandling(ASSET_CATEGORY_QUERY, {
    skip: !assetCategoryId,
    variables: { id: assetCategoryId },
    onCompleted: data => {
      setCategory(data?.assetCategory?.name);
    }
  });

  useQueryWithSimpleErrorHandling(ASSET_SUBCATEGORY_QUERY, {
    skip: insuranceTypeCode !== rcaInsuranceTypeCode || !(assetLens.subcategoryId |> get),
    variables: { id: assetLens.subcategoryId |> get },
    onCompleted: data => {
      setSubcategory(data?.assetSubcategory?.name);
    }
  });

  useEffect(() => {
    const unsavedChanges =
      isDirty(assetDirtyInfo) || isDirty(insuredDirtyInfo) || isDirty(clientDirtyInfo) || isDirty(insuranceDataDirtyInfo);
    setSaveDisabled(!unsavedChanges);
  }, [assetDirtyInfo, clientDirtyInfo, insuranceDataDirtyInfo, insuredDirtyInfo]);

  const [saveOfferData] = useMutation(SAVE_OFFER_DATA, {
    onError: error => {
      showError(error);
      setSaveDisabled(false);
      setSaving(false);
    }
  });

  const getPartnerData = useCallback(lens => {
    return {
      partnerId: lens.partnerId |> get,
      partnerTypeId: lens.partnerTypeId |> get,
      uniqueID: lens.uniqueID |> get,
      agreesWithPersonalDataUse: lens.agreesWithPersonalDataUse |> get,
      partnerCode: lens.partnerCode |> get,
      partnerName: lens.partnerName |> get,
      firstName: lens.firstName |> get,
      lastName: lens.lastName |> get,
      contactPersonName: lens.contactPersonName |> get,
      contactPersonUniqueID: lens.contactPersonUniqueID |> get,
      chContactPersonId: lens.chContactPersonId |> get,
      citizenshipId: lens.citizenshipId |> get,
      nationalityId: lens.nationalityId |> get,
      identityCardNo: lens.identityCardNo |> get,
      issuedBy: lens.issuedBy |> get,
      identityDocumentIssueDate: lens.identityDocumentIssueDate |> get,
      identityDocumentExpireDate: lens.identityDocumentExpireDate |> get,
      identityDocumentTypeId: lens.identityDocumentTypeId |> get,
      phoneNumber: lens.phoneNumber |> get,
      phoneCountryCode: lens.phoneCountryCode |> get,
      email: lens.email |> get,
      cityId: lens.city.id |> get,
      districtId: lens.districtId |> get,
      street: lens.street |> get,
      streetNumber: lens.streetNumber |> get,
      buildingNumber: lens.buildingNumber |> get,
      stairNumber: lens.stairNumber |> get,
      floorNumber: lens.floorNumber |> get,
      flatNumber: lens.flatNumber |> get,
      zipCode: lens.zipCode |> get,
      fullAddress: lens.fullAddress |> get,
      activityFieldId: lens.activityFieldId |> get,
      usageTypeId: lens.usageTypeId |> get,
      drivingLicenseReleaseDate: lens.drivingLicenseReleaseDate |> get,
      validationMoment: lens.validationMoment |> get,
      masterPartnerValidationMoment: lens.masterPartnerValidationMoment |> get,
      isSystem: lens.isSystem |> get,
      masterPartnerId: lens.masterPartnerId |> get,
      chPartnerId: lens.chPartnerId |> get
    };
  }, []);

  const getPartnersData = useCallback(() => {
    if (clientId === insuredId)
      return {
        insured: getPartnerData(insuredLens),
        insuredIsClient: true
      };
    else
      return {
        client: getPartnerData(clientLens),
        insured: getPartnerData(insuredLens),
        insuredIsClient: false
      };
  }, [clientId, clientLens, getPartnerData, insuredId, insuredLens]);

  const handleToggleShowEditableFields = useCallback(() => {
    setShowEditableFields(prev => !prev);
  }, []);

  const handleSave = useCallback(() => {
    const partnersInputData = getPartnersData();

    const input = {
      documentId,
      startDate: insuranceDataLens.startDate |> get,
      asset: {
        assetId: assetLens.assetId |> get,
        chassisNumber: assetLens.chassisNumber |> get,
        vehicleCategoryCode: assetLens.vehicleCategoryCode |> get,
        rcaCategoryId: assetLens.rcaCategoryId |> get,
        cascoCategoryId: assetLens.cascoCategoryId |> get,
        subcategoryId: assetLens.subcategoryId |> get,
        assetHierarchyId: assetLens.assetHierarchyId |> get,
        registrationTypeId: assetLens.registrationTypeId |> get,
        registrationNumber: assetLens.registrationNumber |> get,
        engineType: assetLens.engineType |> get,
        otherAssetName: assetLens.otherAssetName |> get,
        cylindricCapacity: assetLens.cylindricCapacity |> get,
        enginePower: assetLens.enginePower |> get,
        maxWeight: assetLens.maxWeight |> get,
        seatsNumber: assetLens.seatsNumber |> get,
        manufacturingYear: assetLens.manufacturingYear |> get,
        manufacturingMonth: assetLens.manufacturingMonth |> get,
        identityCardSeries: assetLens.identityCardSeries |> get,
        currentMileage: assetLens.currentMileage |> get,
        purchasePrice: assetLens.purchasePrice |> get,
        purchaseInvoiceDate: assetLens.purchaseInvoiceDate |> get,
        firstRegistrationDate: assetLens.firstRegistrationDate |> get,
        equipment: assetLens.equipment |> get
      },
      ...partnersInputData
    };
    if (!validate(input)) {
      setWarningOnEditButton(true);
      return;
    }

    setWarningOnEditButton(false);
    setSaving(true);
    setSaveDisabled(true);
    saveOfferData({
      variables: {
        input: { ...input }
      }
    });
  }, [validate, getPartnersData, saveOfferData, documentId, insuranceDataLens.startDate, assetLens]);

  useEffect(() => {
    const partnersInputData = getPartnersData();
    const input = {
      documentId,
      startDate: insuranceDataLens.startDate |> get,
      asset: {
        assetId: assetLens.assetId |> get,
        chassisNumber: assetLens.chassisNumber |> get,
        vehicleCategoryCode: assetLens.vehicleCategoryCode |> get,
        rcaCategoryId: assetLens.rcaCategoryId |> get,
        cascoCategoryId: assetLens.cascoCategoryId |> get,
        subcategoryId: assetLens.subcategoryId |> get,
        assetHierarchyId: assetLens.assetHierarchyId |> get,
        registrationTypeId: assetLens.registrationTypeId |> get,
        registrationNumber: assetLens.registrationNumber |> get,
        engineType: assetLens.engineType |> get,
        otherAssetName: assetLens.otherAssetName |> get,
        cylindricCapacity: assetLens.cylindricCapacity |> get,
        enginePower: assetLens.enginePower |> get,
        maxWeight: assetLens.maxWeight |> get,
        seatsNumber: assetLens.seatsNumber |> get,
        manufacturingYear: assetLens.manufacturingYear |> get,
        manufacturingMonth: assetLens.manufacturingMonth |> get,
        identityCardSeries: assetLens.identityCardSeries |> get,
        currentMileage: assetLens.currentMileage |> get,
        purchasePrice: assetLens.purchasePrice |> get,
        purchaseInvoiceDate: assetLens.purchaseInvoiceDate |> get,
        firstRegistrationDate: assetLens.firstRegistrationDate |> get,
        equipment: []
      },
      ...partnersInputData
    };
    validate(input, { ...assetDirtyInfo, ...insuredDirtyInfo, ...clientDirtyInfo, ...insuranceDataDirtyInfo });
  }, [
    assetDirtyInfo,
    assetLens,
    clientDirtyInfo,
    documentId,
    getPartnersData,
    insuranceDataDirtyInfo,
    insuranceDataLens.startDate,
    insuredDirtyInfo,
    validate
  ]);

  useConditionalSubscription(OFFER_DATA_SAVED, {
    variables: { documentId },
    skip: !documentId,
    onData: ({ data: { data } }) => {
      if (data.offerDataSaved) {
        resetInsuranceDataLens({ ...(insuranceDataLens |> get), ...data.offerDataSaved.insuranceData });
        resetAssetLens({ ...(assetLens |> get), ...data.offerDataSaved.asset });
        resetInsuredLens({ ...(insuredLens |> get), ...data.offerDataSaved.insured });
        resetClientLens({ ...(clientLens |> get), ...data.offerDataSaved.client });
        setSaveDisabled(false);
        setSaving(false);
        setShowEditableFields(false);
        addToast(t('General.Saved'), 'success', { toastId: 'offerDataSavedToast' });
      }
    }
  });

  useEffect(() => {
    if (insuranceData) {
      resetInsuranceDataLens(insuranceData);
    }
  }, [insuranceData, resetInsuranceDataLens]);

  const isAddressCompleted = (lens, dirtyInfo) => {
    return Boolean(lens.street |> get) && Boolean(lens.streetNumber |> get) && !isDirty(dirtyInfo);
  };

  const isAssetCompleted = useMemo(() => {
    return Boolean(assetLens.identityCardSeries |> get) && !isDirty(assetDirtyInfo);
  }, [assetLens.identityCardSeries, assetDirtyInfo]);

  const isInsuredAddressCompleted = useMemo(() => {
    return isAddressCompleted(insuredLens, insuredDirtyInfo);
  }, [insuredLens, insuredDirtyInfo]);

  const isClientAddressCompleted = useMemo(() => {
    return isAddressCompleted(clientLens, clientDirtyInfo);
  }, [clientLens, clientDirtyInfo]);

  const disabledStatus = useMemo(() => {
    return {
      isAssetCompleted,
      isInsuredAddressCompleted,
      isClientAddressCompleted,
      fullCompleted:
        isAssetCompleted &&
        (isInsuredAddressCompleted || (insuredLens.isSystem |> get)) &&
        (isClientAddressCompleted || (clientLens.isSystem |> get) || insuredId === clientId),
      isGenerationFlowBlocked: insuranceTypeCode === rcaInsuranceTypeCode && negotiatedQuotation
    };
  }, [
    isAssetCompleted,
    isInsuredAddressCompleted,
    isClientAddressCompleted,
    insuredLens.isSystem,
    clientLens.isSystem,
    insuredId,
    clientId,
    insuranceTypeCode,
    negotiatedQuotation,
    rcaInsuranceTypeCode
  ]);

  return (
    <>
      {hasRightToViewOfferData && (
        <OfferDataCard
          insuranceDataLens={insuranceDataLens}
          insuranceTypeCode={insuranceTypeCode}
          rcaInsuranceTypeCode={rcaInsuranceTypeCode}
          cascoInsuranceTypeCod={cascoInsuranceTypeCode}
          assetLens={assetLens}
          assetLoading={assetLoading}
          category={category}
          subcategory={subcategory}
          saving={saving}
          saveDisabled={saveDisabled}
          showEditableFields={showEditableFields}
          insuredLens={insuredLens}
          insuredLoading={insuredLoading}
          insuredId={insuredId}
          clientLens={clientLens}
          clientLoading={clientLoading}
          clientId={clientId}
          disabledStatus={disabledStatus}
          validation={validation}
          warningOnEditButton={warningOnEditButton}
          documentDataHeader={documentDataHeader}
          onSave={handleSave}
          onToggleShowEditableFields={handleToggleShowEditableFields}
        />
      )}
    </>
  );
};

OfferDataContainer.propTypes = {
  documentId: PropTypes.number.isRequired,
  clientId: PropTypes.number,
  insuredId: PropTypes.number,
  negotiatedQuotation: PropTypes.bool,
  insuranceTypeCode: PropTypes.string,
  rcaInsuranceTypeCode: PropTypes.string,
  cascoInsuranceTypeCode: PropTypes.string,
  insuranceData: PropTypes.object
};

export default OfferDataContainer;
